import React, { useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import {
  AuthApiError,
  loginAuth,
  MOJANG_USERNAME_ERROR_CODE,
  registerAuth,
} from '../../api/utopiaAuthApi'
import {
  refreshUtopiaIpGameSession,
  setUtopiaAuthSession,
  utopiaAuthState,
  utopiaAuthUiState,
  type UtopiaMicrosoftVerifiedProfile,
} from './utopiaAuthState'
import { openUtopiaCguModal } from './utopiaOverlayState'
import UtopiaAccountMenu from './UtopiaAccountMenu'
import UtopiaMicrosoftAuthBlock from './UtopiaMicrosoftAuthBlock'
import UtopiaBedrockConsoleRegister from './UtopiaBedrockConsoleRegister'
import styles from './utopiaLanding.module.css'

type Tab = 'login' | 'register'
type RegisterMode = 'standard' | 'bedrock_console'

type Props = {
  idPrefix?: string
  onSuccess?: () => void
  onClose?: () => void
  onOptionsClick?: () => void
  onPlayClick?: () => void
}

export default function UtopiaAuthForm ({
  idPrefix = 'utopia-auth',
  onSuccess,
  onClose,
  onOptionsClick,
  onPlayClick,
}: Props) {
  const { user } = useSnapshot(utopiaAuthState)
  const { microsoftVerifiedProfile } = useSnapshot(utopiaAuthUiState)
  const [tab, setTab] = useState<Tab>('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [registerMode, setRegisterMode] = useState<RegisterMode>('standard')
  const [bedrockConsoleCode, setBedrockConsoleCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const isNavDropdown = idPrefix === 'utopia-nav-auth'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (tab === 'register' && !acceptedTerms) {
      setError('Vous devez accepter les conditions générales d\'utilisation')
      return
    }
    if (
      tab === 'register'
      && registerMode === 'standard'
      && microsoftVerifiedProfile
      && username.trim().toLowerCase() !== microsoftVerifiedProfile.name.toLowerCase()
    ) {
      setError('Le pseudo doit être identique à votre compte Minecraft Microsoft.')
      return
    }
    if (tab === 'register' && registerMode === 'bedrock_console') {
      if (bedrockConsoleCode.trim().length !== 8) {
        setError('Saisissez le code à 8 caractères affiché en jeu sur la console.')
        return
      }
      if (!username.trim()) {
        setError('Code invalide ou pseudo non récupéré. Vérifiez le code et votre réseau.')
        return
      }
    }
    setLoading(true)
    try {
      const msMatch = microsoftVerifiedProfile
        && username.trim().toLowerCase() === microsoftVerifiedProfile.name.toLowerCase()
      const proofToken = msMatch && microsoftVerifiedProfile.proofExpiresAt > Date.now()
        ? microsoftVerifiedProfile.proofToken
        : undefined
      if (msMatch && !proofToken) {
        setError('La preuve Microsoft a expiré. Reconnectez-vous avec le bouton Microsoft.')
        return
      }
      const result = tab === 'login'
        ? await loginAuth(username, password, proofToken)
        : await registerAuth(
          username,
          password,
          email || undefined,
          registerMode === 'standard' ? proofToken : undefined,
          registerMode === 'bedrock_console' ? bedrockConsoleCode : undefined,
        )
      setUtopiaAuthSession({ user: result.user })
      void refreshUtopiaIpGameSession()
      onSuccess?.()
      onClose?.()
    } catch (err) {
      if (
        tab === 'register'
        && err instanceof AuthApiError
        && err.code === MOJANG_USERNAME_ERROR_CODE
      ) {
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setLoading(false)
    }
  }

  if (user) {
    return (
      <UtopiaAccountMenu
        onClose={onClose}
        onPlayClick={onPlayClick}
        onOptionsClick={onOptionsClick}
      />
    )
  }

  const handleMicrosoftVerified = (profile: UtopiaMicrosoftVerifiedProfile) => {
    setRegisterMode('standard')
    setBedrockConsoleCode('')
    setUsername(profile.utopiaUsername)
    setPassword('')
    setEmail('')
    setAcceptedTerms(false)
    setError(null)
    if (profile.utopiaAccountExists) {
      setTab('login')
    } else {
      setTab('register')
    }
  }

  useEffect(() => {
    if (tab !== 'register') {
      setRegisterMode('standard')
      setBedrockConsoleCode('')
    }
  }, [tab])

  const showStandardRegister = tab !== 'register' || registerMode === 'standard'
  const bedrockPseudoResolved = tab === 'register'
    && registerMode === 'bedrock_console'
    && !!username.trim()

  const authDivider = (
    <div className={styles.authMicrosoftDivider}>
      <span>ou</span>
    </div>
  )

  return (
    <>
      {showStandardRegister ? (
        <>
          <UtopiaMicrosoftAuthBlock onVerified={handleMicrosoftVerified} />
          {authDivider}
        </>
      ) : null}

      {tab === 'register' && registerMode === 'standard' ? (
        <>
          <div className={styles.authBedrockConsoleBlock}>
            <button
              type="button"
              className={styles.authBedrockConsoleBtn}
              onClick={() => {
                setRegisterMode('bedrock_console')
                setBedrockConsoleCode('')
                setUsername('')
                setPassword('')
                setEmail('')
                setAcceptedTerms(false)
                setError(null)
                utopiaAuthUiState.microsoftVerifiedProfile = null
              }}
            >
              Inscription Bedrock Console
            </button>
          </div>
          {authDivider}
        </>
      ) : null}

      {tab === 'register' && registerMode === 'bedrock_console' ? (
        <>
          <UtopiaBedrockConsoleRegister
            idPrefix={idPrefix}
            code={bedrockConsoleCode}
            onCodeChange={setBedrockConsoleCode}
            username={username}
            onUsernameResolved={setUsername}
            onBack={() => {
              setRegisterMode('standard')
              setBedrockConsoleCode('')
              setError(null)
            }}
            onError={setError}
          />
          {authDivider}
        </>
      ) : null}

      <div className={styles.authTabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'login'}
          className={`${styles.authTab} ${tab === 'login' ? styles.authTabActive : ''}`}
          onClick={() => {
            setTab('login')
            setRegisterMode('standard')
            setBedrockConsoleCode('')
            setError(null)
            setAcceptedTerms(false)
          }}
        >
          Se connecter
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'register'}
          className={`${styles.authTab} ${tab === 'register' ? styles.authTabActive : ''}`}
          onClick={() => {
            setTab('register')
            setError(null)
          }}
        >
          S&apos;inscrire
        </button>
      </div>

      <form
        className={tab === 'register' ? styles.authFormRegister : undefined}
        onSubmit={e => void submit(e)}
      >
        <div className={styles.authField}>
          <label className={styles.authLabel} htmlFor={`${idPrefix}-username`}>Pseudo</label>
          <input
            id={`${idPrefix}-username`}
            className={styles.authInput}
            autoComplete="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            readOnly={
              bedrockPseudoResolved
              || (
                tab === 'register'
                && !!microsoftVerifiedProfile
                && !microsoftVerifiedProfile.utopiaAccountExists
              )
            }
            required
            minLength={3}
            maxLength={16}
          />
          {tab === 'register' ? (
            <p className={styles.authHint}>
              {bedrockPseudoResolved
                ? 'Pseudo détecté depuis votre session en jeu (non modifiable).'
                : 'Bedrock : le pseudo peut commencer par un point (ex. .MonPseudo).'}
            </p>
          ) : null}
        </div>
        {tab === 'register' && (
          <div className={styles.authField}>
            <label className={styles.authLabel} htmlFor={`${idPrefix}-email`}>
              {isNavDropdown ? 'E-mail' : 'E-mail (optionnel)'}
            </label>
            <input
              id={`${idPrefix}-email`}
              className={styles.authInput}
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
        )}
        <div className={styles.authField}>
          <label className={styles.authLabel} htmlFor={`${idPrefix}-password`}>Mot de passe</label>
          <input
            id={`${idPrefix}-password`}
            className={styles.authInput}
            type="password"
            autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            minLength={tab === 'register' ? 10 : undefined}
          />
          {tab === 'register' ? (
            <p className={styles.authHint}>
              Au moins 10 caractères, une lettre et un chiffre ; évitez les mots courants et votre pseudo.
            </p>
          ) : null}
        </div>
        {tab === 'register' && (
          <label className={styles.authTermsRow}>
            <input
              id={`${idPrefix}-terms`}
              type="checkbox"
              className={styles.authTermsCheckbox}
              checked={acceptedTerms}
              onChange={e => {
                setAcceptedTerms(e.target.checked)
                if (e.target.checked) setError(null)
              }}
              required
            />
            <span className={styles.authTermsText}>
              {isNavDropdown ? (
                <>
                  J&apos;accepte les{' '}
                  <button
                    type="button"
                    className={styles.joinLink}
                    onClick={e => {
                      e.preventDefault()
                      openUtopiaCguModal()
                    }}
                  >
                    CGU
                  </button>.
                </>
              ) : (
                <>
                  En cochant cette case, vous acceptez nos{' '}
                  <button
                    type="button"
                    className={styles.joinLink}
                    onClick={e => {
                      e.preventDefault()
                      openUtopiaCguModal()
                    }}
                  >
                    conditions générales d&apos;utilisation
                  </button>.
                </>
              )}
            </span>
          </label>
        )}
        <div className={styles.authError} role="alert">{error ?? ''}</div>
        <button type="submit" className={styles.authSubmit} disabled={loading}>
          {loading ? '…' : tab === 'login' ? 'Se connecter' : (isNavDropdown ? 'Créer compte' : 'Créer mon compte')}
        </button>
      </form>
    </>
  )
}
